/**
 * A3 — owns the per-slot price scale mode + inverted flag.
 * W0 Task 0.3 — also owns the per-slot axis-side override (absent = PaneSpec side stands).
 *
 * Split out of `KernelEngine` to keep that file inside its frozen fat-file
 * budget. Each setter returns `true` only when the value actually changed,
 * so the caller decides whether to schedule a render.
 */
export class PriceScaleController {
    modes = new Map();
    inverted = new Map();
    sides = new Map();
    logWarned = new Set();
    getMode(scaleSlotId) {
        return this.modes.get(scaleSlotId) ?? "linear";
    }
    getInverted(scaleSlotId) {
        return this.inverted.get(scaleSlotId) ?? false;
    }
    /** W0 Task 0.3: side override, or null when the PaneSpec side stands. */
    getSide(scaleSlotId) {
        return this.sides.get(scaleSlotId) ?? null;
    }
    isLogWarned(scaleSlotId) {
        return this.logWarned.has(scaleSlotId);
    }
    addLogWarned(scaleSlotId) {
        this.logWarned.add(scaleSlotId);
    }
    deleteLogWarned(scaleSlotId) {
        this.logWarned.delete(scaleSlotId);
    }
    setMode(mode, scaleSlotId) {
        if (mode !== "linear" && mode !== "log" && mode !== "percent") {
            console.warn(`[KernelEngine] Ignoring unknown price scale mode: ${String(mode)}`);
            return false;
        }
        const prev = this.modes.get(scaleSlotId);
        if (prev === mode) {
            return false;
        }
        this.modes.set(scaleSlotId, mode);
        this.logWarned.delete(scaleSlotId);
        return true;
    }
    setInverted(inverted, scaleSlotId) {
        const prev = this.inverted.get(scaleSlotId) ?? false;
        if (prev === inverted) {
            return false;
        }
        this.inverted.set(scaleSlotId, inverted);
        return true;
    }
    setSide(side, scaleSlotId) {
        if (side !== "left" && side !== "right") {
            console.warn(`[KernelEngine] Ignoring unknown price axis side: ${String(side)}`);
            return false;
        }
        const prev = this.sides.get(scaleSlotId);
        if (prev === side) {
            return false;
        }
        this.sides.set(scaleSlotId, side);
        return true;
    }
    /**
     * W0 Task 0.3: PaneSpecs with per-slot axis-side overrides applied.
     * Index-aligned with the input, so layout, axis targets, reconcile and
     * snapshots all follow the override. Zero-cost (same ref) when empty.
     */
    withSidesApplied(panes) {
        if (this.sides.size === 0) {
            return panes;
        }
        return panes.map((pane) => ({
            ...pane,
            scaleSlots: pane.scaleSlots.map((slot) => {
                const side = this.sides.get(slot.id);
                return side !== undefined && side !== slot.side ? { ...slot, side } : slot;
            }),
        }));
    }
}
